import { useCallback, useEffect, useReducer, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { formatRM, type Product } from '@bazaar/shared';
import { api } from '../lib/api.js';
import { useCart } from '../context/CartContext.js';
import { useAppConfig } from '../context/AppConfigContext.js';
import {
  ScanDebouncer,
  classifyScannedProduct,
  computeTargetBox,
  initialScanState,
  isWithinTargetBox,
  normalizeScanPayload,
  scanReducer,
  type Point,
} from '../lib/scanner.js';
import { BigButton } from './BigButton.js';

const MUTE_STORAGE_KEY = 'bazaar.scanner.muted';
/** Same-code re-scans within this window are ignored; a different code is always accepted. */
const DEBOUNCE_MS = 1200;
/**
 * Query param that arms the dev/test hook (`window.__bazaarScanner`). We
 * gate on a query param rather than `import.meta.env.MODE` because
 * Playwright's e2e suite runs against a genuine production build (see
 * playwright.config.ts's webServer), so `MODE` is always 'production' there
 * and could never be used to opt back in. The hook only activates when a
 * page explicitly carries `?mockScanner=1` in its URL, which nobody does by
 * accident, so ordinary production use is unaffected even though the code
 * ships in the production bundle.
 */
const MOCK_SCANNER_PARAM = 'mockScanner';

export interface ScannerSheetProps {
  /** Products already loaded on the Sell screen; checked before falling back to the API. */
  products: Product[];
  onClose: () => void;
}

function loadMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function playBeep(): void {
  try {
    const AudioCtx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.value = 0.15;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.08);
    osc.onended = () => void ctx.close();
  } catch {
    // Autoplay restrictions or no WebAudio support - the vibration/visual feedback still fires.
  }
}

/** A small amber "TRAINING" pill pinned to the top-left of the scanner overlay - training-env spec §4. */
function ScannerTrainingPill() {
  const { environment } = useAppConfig();
  if (environment !== 'training') return null;
  return (
    <span
      className="absolute left-2 top-2 z-20 rounded-full px-2 py-0.5 font-heading text-xs font-semibold"
      style={{ backgroundColor: 'var(--training-strip)', color: 'var(--ink)' }}
    >
      TRAINING
    </span>
  );
}

export function ScannerSheet({ products, onClose }: ScannerSheetProps) {
  const cart = useCart();
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(scanReducer, initialScanState);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [muted, setMuted] = useState(loadMuted);
  const [manualCode, setManualCode] = useState('');

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const rafRef = useRef<number | null>(null);
  const zxingControlsRef = useRef<{ stop: () => void } | null>(null);
  const debouncerRef = useRef(new ScanDebouncer(DEBOUNCE_MS));
  const manualInputRef = useRef<HTMLInputElement | null>(null);
  const proceedButtonRef = useRef<HTMLButtonElement | null>(null);

  // Kept fresh via effects below so the long-lived decode loop / dev hook
  // (registered once) always sees the latest products, cart and phase.
  const productsRef = useRef(products);
  const cartRef = useRef(cart);
  const mutedRef = useRef(muted);
  const phaseRef = useRef(state.phase);
  useEffect(() => {
    productsRef.current = products;
  }, [products]);
  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);
  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);
  useEffect(() => {
    phaseRef.current = state.phase;
  }, [state.phase]);

  const handleDecoded = useCallback((rawText: string, opts?: { skipDebounce?: boolean }) => {
    // The choice panel (or a closing sheet) is up - ignore any further
    // decode result, including one fed by the dev/test hook, until "Scan
    // more items" puts us back in `scanning`.
    if (phaseRef.current !== 'scanning') return;

    const code = normalizeScanPayload(rawText);
    if (!code) return;
    const now = Date.now();
    // The debounce window guards the continuous camera decode loop (many
    // frames/sec) against re-triggering on a code that's just sitting in
    // view. Discrete, deliberate scan events - the manual "type a code"
    // fallback and the dev/test hook, each representing exactly one scan -
    // skip it, matching tapping a tile twice to add two.
    if (!opts?.skipDebounce && !debouncerRef.current.accept(code, now)) return;

    navigator.vibrate?.(60);
    if (!mutedRef.current) playBeep();

    void (async () => {
      const currentProducts = productsRef.current;
      const currentCart = cartRef.current;

      let product = currentProducts.find((p) => p.code.toUpperCase() === code) ?? null;
      if (product && !product.isActive) product = null;

      if (!product) {
        try {
          const fetched = await api.getProductByCode(code);
          product = fetched.isActive ? fetched : null;
        } catch {
          product = null;
        }
      }

      const existingLine = product
        ? currentCart.state.lines.find((l) => l.productId === product!.id)
        : undefined;
      const outcome = classifyScannedProduct(product, code, existingLine?.qty ?? 0);

      if (outcome.kind === 'added') {
        currentCart.addItem(outcome.product);
      }
      dispatch({ type: 'outcome', outcome });
    })();
  }, []);

  const stopAll = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    zxingControlsRef.current?.stop();
    zxingControlsRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    trackRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  // Dev/test-only hook - see MOCK_SCANNER_PARAM comment above. Gated behind a
  // build-time env flag too, so the hook is compiled out of ordinary
  // production builds entirely (only the Playwright webServer build sets
  // VITE_ENABLE_SCANNER_MOCK=1) and isn't reachable in production just by
  // guessing the query param.
  useEffect(() => {
    if (import.meta.env.VITE_ENABLE_SCANNER_MOCK !== '1') return;
    if (new URLSearchParams(window.location.search).get(MOCK_SCANNER_PARAM) !== '1') return;
    window.__bazaarScanner = { emit: (code) => handleDecoded(code, { skipDebounce: true }) };
    return () => {
      if (window.__bazaarScanner?.emit === handleDecoded) delete window.__bazaarScanner;
    };
  }, [handleDecoded]);

  // Camera + decode loop lifecycle. Only runs while `scanning`: a
  // successful add moves to `confirmed`, which tears this down (stopping
  // the camera light) via the cleanup below, and "Scan more items" moves
  // back to `scanning`, which re-runs it and re-acquires the camera.
  useEffect(() => {
    if (state.phase !== 'scanning') return;
    let cancelled = false;

    function loopBarcodeDetector(detector: BarcodeDetector) {
      const tick = () => {
        const video = videoRef.current;
        if (video && video.readyState >= 2) {
          detector
            .detect(video)
            .then((results) => {
              for (const r of results) {
                const points: Point[] = (r.cornerPoints ?? []).map((p) => ({ x: p.x, y: p.y }));
                const box = computeTargetBox(video.videoWidth, video.videoHeight);
                if (isWithinTargetBox(points, box)) {
                  handleDecoded(r.rawValue);
                  break;
                }
              }
            })
            .catch(() => {
              // Transient decode errors are expected while framing a shot; keep looping.
            });
        }
        if (!cancelled) rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }

    async function startZxing() {
      const video = videoRef.current;
      const stream = streamRef.current;
      if (!video || !stream) return;
      const { BrowserQRCodeReader } = await import('@zxing/browser');
      if (cancelled) return;
      const reader = new BrowserQRCodeReader();
      const controls = await reader.decodeFromStream(stream, video, (result) => {
        if (!result) return;
        const points: Point[] = result.getResultPoints().map((p) => ({ x: p.getX(), y: p.getY() }));
        const box = computeTargetBox(video.videoWidth, video.videoHeight);
        if (isWithinTargetBox(points, box)) {
          handleDecoded(result.getText());
        }
      });
      if (cancelled) {
        controls.stop();
        return;
      }
      zxingControlsRef.current = controls;
    }

    async function start() {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        setCameraError('Camera requires a secure connection. Type the code instead.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0] ?? null;
        trackRef.current = track;
        const capabilities = track?.getCapabilities?.();
        setTorchSupported(Boolean(capabilities?.torch));
        setTorchOn(false);

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }

        if ('BarcodeDetector' in window && window.BarcodeDetector) {
          const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
          loopBarcodeDetector(detector);
        } else {
          await startZxing();
        }
      } catch (err) {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : '';
        setCameraError(
          name === 'NotAllowedError'
            ? 'Camera permission denied. Type the code instead.'
            : 'Could not access the camera. Type the code instead.',
        );
      }
    }

    void start();
    return () => {
      cancelled = true;
      stopAll();
    };
  }, [handleDecoded, stopAll, state.phase]);

  // Focus/announce management for the confirm panel and the manual-entry
  // fallback, on every transition into `scanning`.
  useEffect(() => {
    if (state.phase === 'confirmed') {
      proceedButtonRef.current?.focus();
    } else if (state.phase === 'scanning' && cameraError) {
      // No camera to restart in the manual-fallback path - land focus back
      // on the code input instead.
      manualInputRef.current?.focus();
    }
  }, [state.phase, cameraError]);

  async function toggleTorch() {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch {
      // Some browsers report torch capability but reject the constraint; nothing more we can do.
    }
  }

  function toggleMute() {
    setMuted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(MUTE_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // Storage unavailable (private browsing) - the toggle still works for this session.
      }
      return next;
    });
  }

  function handleManualSubmit(e: FormEvent) {
    e.preventDefault();
    const raw = manualCode.trim();
    if (!raw) return;
    setManualCode('');
    handleDecoded(raw, { skipDebounce: true });
  }

  function handleClose() {
    dispatch({ type: 'close' });
    stopAll();
    onClose();
  }

  function handleScanMore() {
    dispatch({ type: 'scanMore' });
  }

  function handleProceedToCheckout() {
    handleClose();
    navigate('/cart');
  }

  const itemCount = cart.totals.itemCount;
  const totalLabel = `${itemCount} item${itemCount === 1 ? '' : 's'} · ${formatRM(cart.totals.totalCents)}`;

  if (state.phase === 'confirmed' && state.outcome?.kind === 'added') {
    const { product, qtyInCart } = state.outcome;
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Scan a product code"
        className="fixed inset-0 z-50 bg-black flex flex-col"
      >
        <ScannerTrainingPill />
        <div className="relative z-10 flex items-center justify-end p-4">
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close scanner"
            className="h-12 w-12 rounded-full bg-white/20 text-white text-2xl flex items-center justify-center"
          >
            &times;
          </button>
        </div>

        <div
          role="status"
          aria-live="polite"
          className="flex-1 flex flex-col items-center justify-center gap-6 px-6 py-8 overflow-y-auto"
        >
          <div className="card card-stack w-full max-w-sm bg-surface">
            <div className="flex items-center gap-3">
              <div className="h-16 w-16 shrink-0 rounded-lg bg-teal-dark flex items-center justify-center overflow-hidden">
                {product.thumbUrl ? (
                  <img src={product.thumbUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="block w-full max-w-full truncate text-white font-heading font-bold text-sm px-1 text-center">
                    {product.code}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="font-body font-semibold text-base text-text truncate">
                  {product.name}
                </p>
                <p className="font-body text-sm text-muted">{product.code}</p>
                <p className="font-heading font-semibold text-base text-teal-dark">
                  Added to cart &middot; &times;{qtyInCart} in cart
                </p>
              </div>
            </div>

            <p className="text-center font-body font-semibold text-base text-text">{totalLabel}</p>

            <p className="text-center font-heading font-semibold text-lg text-ink">
              Do you have more items to scan?
            </p>

            <div className="flex flex-col gap-3">
              <button
                ref={proceedButtonRef}
                type="button"
                onClick={handleProceedToCheckout}
                className="min-h-12 w-full px-6 rounded-lg font-heading font-bold text-xl bg-teal-dark text-white hover:opacity-90"
              >
                Proceed to checkout
              </button>
              <BigButton variant="outline" className="w-full" onClick={handleScanMore}>
                Scan more items
              </BigButton>
              <button
                type="button"
                onClick={handleClose}
                className="min-h-12 font-body font-semibold text-sm text-teal-dark underline"
              >
                Back to products
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  let statusText: string;
  let statusVariant: 'info' | 'warning' | 'error';
  if (cameraError && !state.outcome) {
    statusText = cameraError;
    statusVariant = 'error';
  } else if (!state.outcome || state.outcome.kind === 'added') {
    // 'added' only ever reaches here transiently (the panel above returns
    // early for it) - treated the same as "no outcome yet" for the status line.
    statusText = 'Point at a code';
    statusVariant = 'info';
  } else if (state.outcome.kind === 'out-of-stock') {
    statusText = `Out of stock: ${state.outcome.product.name}`;
    statusVariant = 'warning';
  } else {
    statusText = `Unknown code ${state.outcome.code}`;
    statusVariant = 'error';
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Scan a product code"
      className="fixed inset-0 z-50 bg-black flex flex-col"
    >
      <ScannerTrainingPill />
      <video
        ref={videoRef}
        playsInline
        muted
        className="absolute inset-0 h-full w-full object-cover"
      />

      {!cameraError && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className="aspect-square w-[60vmin] max-w-[60vh] rounded-2xl border-4 border-white/90"
            style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)' }}
          />
        </div>
      )}

      <div className="relative z-10 flex items-center justify-between p-4">
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close scanner"
          className="h-12 w-12 rounded-full bg-white/20 text-white text-2xl flex items-center justify-center"
        >
          &times;
        </button>

        <div className="flex items-center gap-2">
          {torchSupported && (
            <button
              type="button"
              onClick={() => void toggleTorch()}
              aria-pressed={torchOn}
              aria-label={torchOn ? 'Turn off torch' : 'Turn on torch'}
              className={clsx(
                'h-12 px-4 rounded-full font-heading font-semibold text-base',
                torchOn ? 'bg-orange text-ink' : 'bg-white/20 text-white',
              )}
            >
              Torch
            </button>
          )}
          <button
            type="button"
            onClick={toggleMute}
            aria-pressed={muted}
            aria-label={muted ? 'Unmute scan sound' : 'Mute scan sound'}
            className="h-12 w-12 rounded-full bg-white/20 text-white flex items-center justify-center text-xl"
          >
            {muted ? '🔇' : '🔊'}
          </button>
        </div>
      </div>

      <div className="relative z-10 mt-auto flex flex-col gap-3 p-4 pb-8 bg-gradient-to-t from-black/80 to-transparent">
        <p
          role="status"
          aria-live="polite"
          className={clsx(
            'text-center font-heading font-semibold text-lg',
            statusVariant === 'warning' && 'text-orange',
            statusVariant === 'error' && 'text-orange',
            statusVariant === 'info' && 'text-white',
          )}
        >
          {statusText}
        </p>

        <p className="text-center font-body font-semibold text-sm text-white/90">{totalLabel}</p>

        {cameraError && (
          <form onSubmit={handleManualSubmit} className="flex gap-2">
            <label htmlFor="manual-code" className="sr-only">
              Product code
            </label>
            <input
              ref={manualInputRef}
              id="manual-code"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Type code instead"
              className="flex-1 min-h-12 rounded-lg px-4 text-base font-body bg-white"
            />
            <BigButton type="submit">Add</BigButton>
          </form>
        )}
      </div>
    </div>
  );
}
